'use strict';
/* ==========================================================================
   levels/road.js — step "road" (orientation any): ROAD TO NINE (DESIGN.md §5.3). Six rounds on the real ratification
   record, in historical order, on the ledger (DESIGN.md §2).

   An attempt = 6 rounds from content/bank.road.json, drawn by the model (Ratify.attempt('road', json, {fresh}): at
   least 2 votes, 1 deal, 1 rights round and 1 number-pad step, sorted by `order`). Each round = 1–2 bank instances
   (choice or number pad). Every vote count, date, margin, percent and answer comes from core-local/ratify.js; this file
   never types one in.

   The ledger (left; the whole stage in the portrait dock): 13 rows in ratification order. A row fills (name, month/year,
   a navy yes / brick no tally bar, the vote) when the road reaches it; the round's own state is a highlighted "?" slot
   until the round is done, then it fills and the counter "RATIFIED: n of 9 needed" ticks (Ratify.ratifiedBy /
   the row's order). Rounds without a state (the Bill of Rights) light the funnel under the ledger instead (House 17,
   Senate 12, States 10 = Ratify.bor) and move the counter to their date (Ratify.convention / Ratify.bor).
   The question card (right): ROUND n OF 6, the question, then
     choice   options (1–4); a wrong pick is crossed out with its why, retry until right
     numpad   a display + a pad (digits, ⌫, ENTER); a wrong entry shows its why (Bank.why) and a retry; a second wrong
              entry shows the answer and the working. A right entry shows the working (the item's explain).
   Stars: a round whose every step is right on the first try = 1 star; of 6, pass = bank.road.json passStars (4).
   Game.progress.scores.road = {best, last, of: 6}; first answers under Game.progress.answers['road/<round>/<step>'].
   A pass → "Collect your stamp" (Game.complete); leaving a passed attempt without collecting still keeps the stamp.
   Attempt + seed: Game.progress.bank.road = {pool, seed, attempt, ids, prev, open, run: {i, ev, res}}: a reload mid-
   attempt resumes the same round with the same picks and entries (?seed= / window.__bankSeed force a seed).

   Portrait phone (Dock.active): the ledger (two columns) + the counter + the funnel are the picture; the question, the
   options, the number pad (1–5, 6–0, then ⌫ ENTER), the working and the buttons are dock widgets.
   Keys: 1–4 = option, 0–9 / Backspace / Enter = the pad, Enter / Space = start / next.

   QA hooks: sc.phase ('intro' | 'ask' | 'done' | 'end'), sc.round (0–5), sc.k (the step in the round), sc.cur (the
   round), sc.inst (the step's bank instance), sc.kind ('choice' | 'numpad'), sc.rounds, sc.seed, sc.attempt,
   sc.resumed, sc.results, sc.stars, sc.typed, sc.tries, sc.stepDone, sc.answer() / sc.solution (the right option index,
   or the right number as a string), sc.begin(fresh), sc.start(), sc.choose(i), sc.press(key), sc.type(str), sc.enter_() (= ENTER),
   sc.submit(str), sc.next(), sc.collect(), sc.done(), sc.finish(), sc.counter(), sc.filled(); Levels.road.solution,
   Levels.road.preview(ids) (swap the attempt's rounds for these item ids: screenshots of the longest questions).
   ========================================================================== */
(function () {
  const STEP = 'road', TITLE = 'Road to Nine';
  const CV = () => window.Civic;
  const C = () => CV().C;
  const json = () => Game.content.bank_road || { items: [], draw: {} };
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", TYPE = "'Special Elite'", HAND = "'Caveat Brush'";
  const INK = '#2b2420', PENCIL = '#5b6475';
  const BOTTOM = 1066, MAXLEN = 4, NEXT_GUARD = 0.3, FILL = 0.55;
  const PAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'back', '0', 'enter'];
  const DOCK_PAD = [['1', '2', '3', '4', '5'], ['6', '7', '8', '9', '0'], ['back', 'enter']];
  const sfx = (name, alt, o) => { const n = Audio.has(name) ? name : alt; if (n) Audio.play(n, o); };

  /* ---------------- text helpers ---------------- */
  const _mw = new Map();
  const measure = (s, font) => {
    const key = font + '|' + s; let w = _mw.get(key);
    if (w == null) { g.save(); g.font = font; w = g.measureText(s).width; g.restore(); if (_mw.size > 3000) _mw.clear(); _mw.set(key, w); }
    return w;
  };
  const wrap = (t, w, font) => UI._wrap(String(t), w, font);
  function text(lines, x, yTop, lh, font, color, align = 'left') {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = align; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  /* shrink a font size until the string fits the width */
  const fit = (s, w, size, face, min = 12) => { let f = size; while (f > min && measure(s, `${f}px ${face}`) > w) f -= 1; return f; };
  /* below the HUD: the ⌂ slot and the subtitle line (collage_hud writes it at y 150 + (fs − 44) / 2, fs = UI.text(44)) */
  const minTop = () => { const hs = UI.hudSlot(0), fs = UI.text(44); return Math.max(96, hs.y + hs.r + 18, 150 + (fs - 44) * 0.5 + fs * 0.5 + 14); };

  /* ---------------- dates (the ledger's counter for a round without a state) ---------------- */
  const MON = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  /* "Sep 1787" | "June 1789" → the last day of that month, ISO (Ratify.ratifiedBy compares ISO strings) */
  function isoEnd(shown) {
    const m = /^([A-Za-z]{3})[a-z]*\.? (\d{4})$/.exec(String(shown || '').trim());
    if (!m) return null;
    const k = MON.indexOf(m[1].toLowerCase());
    return k < 0 ? null : `${m[2]}-${String(k + 1).padStart(2, '0')}-31`;
  }
  /* the record date of the rounds that have no ledger row (the Convention, the Bill of Rights) */
  const WHEN = {
    convention: () => Ratify.convention.shown,
    madison: () => Ratify.bor.proposed,
    'congress-sends': () => Ratify.bor.sentShown,
    'three-fourths': () => Ratify.bor.shown,
    leftover: () => Ratify.bor.shown,
  };

  class Road extends Scene {
    get n() { return this.rounds ? this.rounds.length : 6; }
    get of() { return this.n; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 0.6)); }
    get cur() { return this.rounds ? this.rounds[this.round] : null; }
    get inst() { const r = this.cur; return r && (this.phase === 'ask' || this.phase === 'done') ? r.steps[this.k] : null; }
    get kind() { const q = this.inst; return q ? (q.answer === 'numpad' ? 'numpad' : 'choice') : null; }
    get tries() { return (this.st[this.k] || { tries: [] }).tries; }
    get stepDone() { const s = this.st[this.k]; return !!(s && s.done); }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.star : 0), 0); }
    get solution() { const q = this.inst; return !q ? null : q.answer === 'numpad' ? String(q.answerValue) : q.answerIndex; }
    answer() { return this.solution; }

    enter() {
      this.phase = 'intro'; this.rounds = null; this.round = 0; this.k = 0; this.results = []; this.passed = false; this.collected = false;
      this.st = []; this.ev = []; this.typed = ''; this.cues = {}; this._lay = new Map(); this._qc = null;
      Audio.play('paper', { gain: 0.5 });
      this.begin(false);
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Ratify.attempt: seeded, saved in Game.progress.bank.road) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || (Game.progress.bank = {}), prevSt = S[STEP], run = !fresh && prevSt && prevSt.open && prevSt.run ? prevSt.run : null;
      const a = Ratify.attempt(STEP, json(), { fresh });
      this.rounds = a.rounds; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false; this._finished = false; this._qc = null; this._cuts = null;
      if (fresh) { this.startRound(0); return; }
      if (a.resumed && run && this.restore(run)) return;
      this.phase = 'intro'; this.introAt = Game.time;
    }
    start() { if (this.phase === 'intro') { this.startRound(0); return true; } return false; }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.rounds.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.roundAt = Game.time - 5; this.qAt = Game.time - 1;
        this.phase = 'ask';
        this.quiet = true;
        for (const e of run.ev || []) {
          if (e[0] === 'c') this.choose(e[1]);
          else if (e[0] === 'n') this.submit(e[1]);
          else if (e[0] === 'x') this.next(true);
        }
        this.quiet = false;
        this.stepAt = Game.time - 5; this.qAt = Game.time - 1;
        if (this.phase === 'done') this.revealAt = Game.time - 20;
        this.cues = { all: 1 };
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[road] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.round = i; this.k = 0; this.ev = []; this.typed = ''; this.cues = {};
      this.st = this.rounds[i].steps.map(() => ({ tries: [], right: false, done: false }));
      this.roundAt = Game.time; this.qAt = Game.time + 0.35; this.stepAt = null; this.pickedAt = null; this.revealAt = null; this.bumpAt = -1e9; this.keyAt = {};
      this._qc = null;
    }
    startRound(i) {
      this.setup(i); this.phase = 'ask';
      this.save();
      this.play('paper', 'whoosh', { gain: 0.5 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end' && this.phase !== 'intro') S.run = { i: this.round, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    play(name, alt, o) { if (!this.quiet) sfx(name, alt, o); }

    /* ---- answering ---- */
    choose(i) {
      const q = this.inst, s = this.st[this.k];
      if (this.phase !== 'ask' || !q || q.answer === 'numpad' || s.done) return false;
      if (!(i >= 0 && i < q.options.length) || s.tries.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!s.tries.length && !this.quiet) Game.progress.answer(`${STEP}/${this.cur.key}/${this.k + 1}`, q.options[i]);   // first try only
      s.tries.push(i); this.pickedAt = Game.time;
      if (i === q.answerIndex) { s.right = true; s.done = true; this.play('ding', null, { gain: 0.6 }); this.stepResolved(); }
      else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this._qc = null;
      this.save();
      return true;
    }
    /* the pad: '0'–'9', 'back', 'enter' */
    press(key) {
      if (this.phase === 'intro') { if (key === 'enter') this.start(); return; }
      if (this.phase === 'end') { if (key === 'enter') this.endButtons()[0].go(); return; }
      if (key === 'enter') { this.enter_(); return; }
      if (this.phase !== 'ask' || this.kind !== 'numpad' || this.stepDone) return;
      this.keyAt[key] = Game.time;
      if (key === 'back') { this.typed = this.typed.slice(0, -1); return; }
      if (!/^[0-9]$/.test(key)) return;
      if (this.typed.length >= MAXLEN) { this.bumpAt = Game.time; return; }
      this.typed = this.typed === '0' ? key : this.typed + key;
    }
    type(str) { for (const ch of String(str)) this.press(ch); return this.typed; }
    enter_() {
      if (this.phase === 'intro') return this.start();
      if (this.phase === 'end') { this.endButtons()[0].go(); return true; }
      if (this.phase === 'done') return this.next();
      if (this.phase !== 'ask') return false;
      if (this.stepDone) return this.next();
      if (this.kind === 'numpad') return this.submit(this.typed);
      return false;
    }
    submit(v) {
      const q = this.inst, s = this.st[this.k];
      if (this.phase !== 'ask' || !q || q.answer !== 'numpad' || s.done) return null;
      v = String(v == null ? '' : v).trim();
      if (!/^[0-9]{1,4}$/.test(v)) { this.bumpAt = Game.time; return null; }
      this.ev.push(['n', v]);
      if (!s.tries.length && !this.quiet) Game.progress.answer(`${STEP}/${this.cur.key}/${this.k + 1}`, v);
      s.tries.push(v); this.pickedAt = Game.time; this.typed = '';
      const right = !!Bank.check(q, v);
      if (right) { s.right = true; s.done = true; this.play('ding', null, { gain: 0.6 }); }
      else if (s.tries.length >= 2) { s.done = true; this.play('wahwah', 'thunk', { gain: 0.3 }); }
      else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      if (s.done) this.stepResolved();
      this._qc = null;
      this.save();
      return right;
    }
    /* a step is resolved: the last one closes the round (the row fills), else the working + "Next question" */
    stepResolved() {
      this.stepAt = Game.time;
      if (this.k >= this.cur.steps.length - 1) this.toDone();
    }
    toDone() {
      const r = this.cur, first = this.st.every(s => s.right && s.tries.length === 1);
      this.phase = 'done'; this.revealAt = Game.time; this.cues = {};
      this.results[this.round] = { round: this.round, id: r.id, title: r.title, state: r.state, star: first ? 1 : 0,
        wrong: this.st.map(s => s.tries.length - (s.right ? 1 : 0)) };
      this._qc = null;
    }
    next(force) {
      if (!force && this.stepAt != null && Game.time - this.stepAt < NEXT_GUARD) return false;
      if (this.phase === 'ask' && this.stepDone && this.k < this.cur.steps.length - 1) {
        this.ev.push(['x']);
        this.k++; this.typed = ''; this.qAt = Game.time + 0.1; this.stepAt = null; this.pickedAt = null; this._qc = null;
        this.save();
        return true;
      }
      if (this.phase !== 'done') return false;
      if (this.round < this.rounds.length - 1) this.startRound(this.round + 1);
      else this.close();
      return true;
    }
    close() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Ratify.finish(STEP);                                                           // the next attempt draws new rounds
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    done() {
      if (!this.rounds) this.begin();
      if (this.phase !== 'end') this.close();
      this.collected = true; Game.complete(STEP);
    }
    finish() { if (this._finished) return; this._finished = true; this.done(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new rounds)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new rounds)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    /* ---------------- the ledger's state: which rows are filled, when (a pure function of the clock) ---------------- */
    /* the rows filled once round j is done: its state's order, or (no state) the count ratified by its date */
    roundCut(j) {
      const r = this.rounds[j], s = r.state && Ratify.st(r.state);
      if (s) return s.order;
      const w = WHEN[r.id], iso = w ? isoEnd(w()) : null;
      return iso ? Ratify.ratifiedBy(iso) : (j > 0 ? this.roundCut(j - 1) : 0);
    }
    cuts() {
      if (this._cuts && this._cuts.rounds === this.rounds) return this._cuts;
      const end = [], start = [];
      let F = 0;
      this.rounds.forEach((r, j) => {
        const s = r.state && Ratify.st(r.state), c = this.roundCut(j);
        start.push(Math.max(F, s ? s.order - 1 : c));
        F = Math.max(F, c); end.push(F);
      });
      return (this._cuts = { rounds: this.rounds, start, end });
    }
    /* when row `o` (1–13) fills: -1e9 (already), a time (animating in), or null (not yet) */
    fillAt(o) {
      if (!this.rounds || this.phase === 'intro') return null;
      const K = this.cuts();
      if (this.phase === 'end') return o <= K.end[K.end.length - 1] ? -1e9 : null;
      const i = this.round, prev = i > 0 ? K.end[i - 1] : 0;
      if (o <= prev) return -1e9;
      if (o <= K.start[i]) return this.roundAt + 0.2 + (o - prev - 1) * 0.12;
      if (o <= K.end[i] && this.phase === 'done' && this.revealAt != null) return this.revealAt + 0.25;
      return null;
    }
    filled() { let n = 0; for (let o = 1; o <= 13; o++) { const a = this.fillAt(o); if (a != null && T >= a) n++; } return n; }
    counter() { return this.filled(); }
    lastFill() { let t = -1e9; for (let o = 1; o <= 13; o++) { const a = this.fillAt(o); if (a != null && T >= a && a > t) t = a; } return t; }
    hlRow() { const r = this.cur; if (!r || !(this.phase === 'ask' || this.phase === 'done')) return 0; const s = r.state && Ratify.st(r.state); return s ? s.order : 0; }
    rightsRound() { const r = this.cur; return !!r && (this.phase === 'ask' || this.phase === 'done') && !r.state; }
    dateOf(r) { const s = r && r.state && Ratify.st(r.state); return s ? s.shown : ''; }

    update() {
      if (this.quiet || !this.rounds) return;
      // a row landing on the ledger: a stamp (once each); nine = official
      for (let o = 1; o <= 13; o++) {
        const a = this.fillAt(o);
        if (a == null || a < -1e8 || T < a || this.cues['r' + o]) continue;
        this.cues['r' + o] = 1;
        if (this.cues.all) continue;
        sfx('stamp', 'thunk', { gain: 0.45, rate: 1.1 });
        if (o === Ratify.article7) sfx('sparkle', null, { gain: 0.6 });
      }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.start(); return; }
      if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); return; }
      if (this.phase === 'done') { if (k === 'Enter' || k === ' ') this.next(); return; }
      if (this.phase !== 'ask') return;
      if (this.stepDone) { if (k === 'Enter' || k === ' ') this.next(); return; }
      if (this.kind === 'numpad') {
        if (/^[0-9]$/.test(k)) this.press(k);
        else if (k === 'Backspace' || k === 'Delete') this.press('back');
        else if (k === 'Enter') this.press('enter');
      } else if (n >= 1 && n <= this.inst.options.length) this.choose(n - 1);
    }

    /* ---------------- layout ---------------- */
    /* the ledger: landscape = a column on the left; docked = the whole stage, two columns */
    ledgerL() {
      const docked = UI.docked(), key = docked + '|' + Math.round(minTop()) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this._lay.get('ledger' + key);
      if (L) return L;
      if (docked) {
        const x = 20, w = 1880, top = 16, h = 1080 - 32, headH = 128, gap = 80, colW = (w - 60 - gap) / 2;
        const rowsTop = top + headH + 16, rowH = Math.floor((top + h - 20 - rowsTop) / 7);
        const cols = [{ x: x + 30, w: colW }, { x: x + 30 + colW + gap, w: colW }];
        const pos = o => { const c = o <= 7 ? cols[0] : cols[1], j = o <= 7 ? o - 1 : o - 8; return { x: c.x, y: rowsTop + j * rowH, w: c.w, h: rowH }; };
        const funnel = { x: cols[1].x, y: rowsTop + 6 * rowH + 6, w: colW, h: rowH - 8 };
        L = { docked, x, w, top, h, head: { x: x + 36, y: top + headH / 2, size: 58 }, counter: { x: x + w - 36 - 620, y: top + 18, w: 620, h: headH - 36, size: 46 },
          pos, rowH, funnel, colW };
      } else {
        const x = 24, w = 812, top = minTop(), h = BOTTOM - top, headH = 76, funH = 128;
        const rowsTop = top + headH + 10, rowH = Math.floor((top + h - funH - 22 - rowsTop) / 13);
        const pos = o => ({ x: x + 22, y: rowsTop + (o - 1) * rowH, w: w - 44, h: rowH });
        L = { docked, x, w, top, h, head: { x: x + 30, y: top + 38, size: 30 }, counter: { x: x + w - 26 - 390, y: top + 12, w: 390, h: 54, size: 24 },
          pos, rowH, funnel: { x: x + 22, y: top + h - funH - 10, w: w - 44, h: funH }, colW: w - 44 };
      }
      // the row's columns (fractions of the row width) and one font size for every name
      const rw = L.colW, rh = L.rowH;
      L.cw = { num: rw * 0.07, name: rw * 0.34, date: rw * 0.19, bar: rw * 0.23, votes: rw * 0.17 };
      const longest = Ratify.STATES.reduce((a, s) => (measure(s.name, `100px ${SERIF}`) > measure(a, `100px ${SERIF}`) ? s.name : a), '');
      L.nameF = fit(longest, L.cw.name - 8, Math.round(rh * 0.5), SERIF);
      L.typeF = fit('Dec 1787', L.cw.date - 10, Math.round(rh * 0.4), TYPE);
      L.voteF = fit('187–168', L.cw.votes - 8, Math.round(rh * 0.42), TYPE);
      this._lay.set('ledger' + key, L);
      return L;
    }

    /* the question card (landscape): rows + positions, shrunk until it fits between minTop and BOTTOM */
    card() {
      const key = [this.phase, this.round, this.k, this.tries.length, this.stepDone, Math.round(UI.text(24)), Math.round(UI.tap(84))].join('|');
      if (this._qc && this._qc.key === key) return this._qc;
      const X = 866, W = 1030, lim = minTop(), r = this.cur;
      const inW = W - 72;
      let R;
      // shrink the type; if even the smallest doesn't fit, a resolved choice step keeps only the right option
      for (let step = 0; step < 24; step++) {
        const f = 1 - (step % 12) * 0.045, compact = step >= 12;
        const ls = Math.round(UI.text(22) * f), qs = Math.round(UI.text(36) * f), os = Math.round(UI.text(30) * f), ws = Math.round(UI.text(32) * f), hs = Math.round(UI.text(38) * f);
        const rows = []; let h = 24;
        const add = (kind, lines, size, lh, extra = 0, o = {}) => { rows.push({ kind, lines, size, lh, y: h, ...o }); h += lines.length * lh + extra; };
        const label = `ROUND ${this.round + 1} OF ${this.n} · ${String(r.title).toUpperCase()}`;
        add('label', [label], ls, ls * 1.4, 2);
        const date = this.dateOf(r);
        let sub = r.state ? `${Ratify.name(r.state)}, ${date}` : 'THE BILL OF RIGHTS';
        if (this.phase === 'ask') sub += `   ·   QUESTION ${this.k + 1} OF ${r.steps.length}`;
        add('label', [sub], ls, ls * 1.4, 12);
        let padArea = null, bh = 0;
        if (this.phase === 'done') {
          const res = this.results[this.round] || { star: 0 };
          add('head', wrap(res.star ? 'Round won: +1 star.' : 'Round done. No star this time.', inW, `${hs}px ${SLAB}`), hs, hs * 1.15, 12);
          r.steps.forEach((q, j) => {
            add('ans', wrap(`${r.steps.length > 1 ? (j + 1) + '. ' : ''}Answer: ${q.answerText}`, inW, `bold ${os}px ${SERIF}`), os, os * 1.25, 2);
            add('work', wrap(q.explain, inW, `${ws}px ${HAND}`), ws, ws * 1.12, 12);
          });
          bh = UI.tap(96) + 24;
        } else {
          const q = this.inst, s = this.st[this.k];
          add('q', wrap(q.text, inW, `600 ${qs}px ${SERIF}`), qs, qs * 1.22, 16);
          if (q.answer === 'numpad') {
            const kh = Math.max(UI.tap(84), Math.round(96 * f)), kw = Math.max(kh, Math.round(146 * f)), gap = 12;
            const padW = kw * 3 + gap * 2, padH = s.done ? 0 : kh * 4 + gap * 3;
            const leftW = s.done ? inW : inW - padW - 34;
            const dh = Math.round(Math.max(UI.tap(84), 108 * f));
            let lh = dh + 16; const left = [];
            const last = s.tries[s.tries.length - 1];
            const note = (kind, str, size, face) => { const ls2 = wrap(str, leftW, `${size}px ${face}`); left.push({ kind, lines: ls2, size, lh: size * 1.14, y: lh }); lh += ls2.length * size * 1.14 + 8; };
            if (s.done) {
              if (!s.right) note('why', `Not ${last}. The answer is ${q.answerText}.`, ws, HAND);
              note('work', q.explain, ws, HAND);
            } else if (last != null) {
              note('why', `Not ${last}. ${Bank.why(q, last) || 'Check your working.'}`, ws, HAND);
              note('retry', 'Try again.', Math.round(ws * 0.9), HAND);
            } else if (UI.keys) note('hint', UI.hint('Type it on the pad or your keyboard, then ENTER.', ''), Math.round(UI.text(24) * f), TYPE);
            padArea = { y: h, kw, kh, gap, padW, padH, leftW, dh, left, h: Math.max(padH, lh) };
            h += padArea.h + 12;
            if (s.done) bh = UI.tap(96) + 24;
          } else {
            q.options.forEach((o, i) => {
              if (compact && s.done && i !== q.answerIndex) return;
              const l2 = wrap(o, inW - 90, `${os}px ${SERIF}`), rh = Math.max(UI.tap(88), l2.length * os * 1.22 + 24);
              rows.push({ kind: 'opt', i, lines: l2, size: os, lh: os * 1.22, y: h, rh }); h += rh + 8;
            });
            const last = s.tries[s.tries.length - 1];
            h += 6;
            if (s.done) { add('work', wrap(q.explain, inW, `${ws}px ${HAND}`), ws, ws * 1.12, 0); bh = UI.tap(96) + 24; }
            else if (last != null) add('why', wrap('Not quite. ' + (q.why[last] || q.explain), inW, `${ws}px ${HAND}`), ws, ws * 1.12, 0);
          }
        }
        h += 22;
        R = { key, X, W, rows, h, bh, padArea, top: Math.max(lim, Math.min(lim + 10, BOTTOM - bh - h)) };
        if (R.top + h + bh <= BOTTOM) break;
      }
      return (this._qc = R);
    }
    sub() {
      if (!this.rounds || this.phase === 'intro' || this.phase === 'end') return '';
      return `round ${this.round + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }

    /* ---------------- drawing ---------------- */
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      UI.hud(TITLE, this.sub());
      this.drawLedger();
      if (docked) return this.drawDock();
      if (this.phase === 'intro') return this.drawIntro();
      if (this.phase === 'end') return this.drawEnd();
      this.drawCardQ();
    }

    /* the ledger: header, the counter, 13 rows, the Bill of Rights funnel */
    drawLedger() {
      const L = this.ledgerL(), c = C(), Cv = CV();
      Cv.parchment(L.x + L.w / 2, L.top + L.h / 2, Math.round(L.w / 10) * 10, Math.round(L.h / 10) * 10, { seed: 2001, depth: 1.4 });
      text(['THE LEDGER'], L.head.x, L.head.y - L.head.size * 0.6, L.head.size * 1.2, `${L.head.size}px ${SLAB}`, c.navy);
      // the counter
      const n = this.filled(), a7 = Ratify.article7, K = L.counter;
      const lf = this.lastFill(), bump = 1 + 0.12 * (1 - clamp((T - lf) / 0.35));
      paperRect(K.x + K.w / 2, K.y + K.h / 2, K.w, K.h, c.navy, { seed: 2002, depth: 0.9, boil: false });
      const str = n < a7 ? `RATIFIED: ${n} of ${a7} needed` : `RATIFIED: ${n} (${a7} needed)`;
      const fs = fit(str, K.w - 30, K.size, SLAB);
      g.save(); g.translate(K.x + K.w / 2, K.y + K.h / 2); g.scale(bump, bump);
      g.font = `${fs}px ${SLAB}`; g.fillStyle = c.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(str, 0, 2);
      g.restore();
      if (n >= a7) {
        const t9 = this.fillAt(a7);
        const s9 = t9 != null && t9 > -1e8 ? pop(T, t9 + 0.2, 0.35, 2.2) : 1;
        // between the header and the counter box
        const hw = measure('THE LEDGER', `${L.head.size}px ${SLAB}`), sx = (L.head.x + hw + K.x) / 2;
        if (s9 > 0) UI.stamp('OFFICIAL', sx, K.y + K.h / 2 + 2, { rot: -0.1, s: s9 * (L.docked ? 0.7 : 0.36), color: c.brick });
      }
      for (const s of Ratify.STATES) this.drawRow(s, L.pos(s.order), L);
      this.drawFunnel(L);
      // the quill by the highlighted row (or the funnel)
      const hr = this.hlRow(), qp = hr ? L.pos(hr) : this.rightsRound() ? { x: L.funnel.x, y: L.funnel.y, w: L.funnel.w, h: L.funnel.h * 0.5 } : null;
      if (qp && IMG.quill && !L.docked) {
        const bob = Math.sin(T * 2.2) * 4, qh = Math.round(L.rowH * 1.9);
        cut('quill', qp.x + qp.w + (L.docked ? -10 : 6), qp.y + qp.h * 0.5 + bob, qh, { ax: 0.1, ay: 0.9, rot: 0.5, seed: 2010, depth: 0.8, boil: false });
      }
    }
    drawRow(s, p, L) {
      const c = C(), at = this.fillAt(s.order), filled = at != null && T >= at, u = filled ? clamp((T - at) / FILL) : 0;
      const hl = s.order === this.hlRow(), cw = L.cw, cy = p.y + p.h / 2;
      if (hl) {
        g.save(); g.fillStyle = 'rgba(176,138,46,0.30)'; g.fillRect(p.x - 6, p.y + 2, p.w + 12, p.h - 4);
        g.strokeStyle = c.gold; g.lineWidth = L.docked ? 5 : 3; g.strokeRect(p.x - 6, p.y + 2, p.w + 12, p.h - 4); g.restore();
      }
      g.save(); g.fillStyle = 'rgba(43,36,32,0.16)'; g.fillRect(p.x, p.y + p.h - 1, p.w, 2); g.restore();
      text([String(s.order)], p.x + cw.num * 0.45, cy - L.typeF * 0.6, L.typeF * 1.2, `${L.typeF}px ${TYPE}`, filled ? c.brick : PENCIL, 'center');
      const x0 = p.x + cw.num;
      if (!filled) {
        if (hl) hand('?', x0 + cw.name * 0.3, cy + 2, Math.round(p.h * 0.7), this.roundAt || -1e6, { color: c.brick, seed: 2020 + s.order, dur: 0.2 });
        else { g.save(); g.fillStyle = 'rgba(91,100,117,0.35)'; for (let x = x0 + 6; x < p.x + p.w - 20; x += 22) g.fillRect(x, cy + p.h * 0.18, 12, 2); g.restore(); }
        return;
      }
      g.save(); g.globalAlpha *= clamp(u * 2.2);
      text([s.name], x0, cy - L.nameF * 0.62, L.nameF * 1.24, `${L.nameF}px ${SERIF}`, INK);
      text([s.shown], x0 + cw.name, cy - L.typeF * 0.6, L.typeF * 1.2, `${L.typeF}px ${TYPE}`, PENCIL);
      // the tally bar: yes (navy) | no (brick), grown in
      const bx = x0 + cw.name + cw.date, bw = cw.bar - 14, bhh = Math.max(10, Math.round(p.h * 0.36)), by = cy - bhh / 2;
      const grow = E.outCubic(u), tot = s.yes + s.no, yw = bw * s.yes / tot * grow, nw = bw * s.no / tot * grow;
      g.fillStyle = 'rgba(43,36,32,0.10)'; g.fillRect(bx, by, bw, bhh);
      g.fillStyle = c.navy; g.fillRect(bx, by, yw, bhh);
      g.fillStyle = c.brick; g.fillRect(bx + yw, by, nw, bhh);
      text([`${s.yes}–${s.no}`], p.x + p.w - 4, cy - L.voteF * 0.6, L.voteF * 1.2, `${L.voteF}px ${TYPE}`, INK, 'right');
      g.restore();
    }
    /* the Bill of Rights funnel: House 17 → Senate 12 → States 10 (Ratify.bor), lit on a rights round */
    drawFunnel(L) {
      const F = L.funnel, c = C(), b = Ratify.bor, on = this.rightsRound();
      const items = [['HOUSE', b.house], ['SENATE', b.senate], ['STATES', b.ratified]];
      g.save(); g.globalAlpha *= on ? 1 : 0.5;
      if (on) { g.fillStyle = 'rgba(176,138,46,0.30)'; g.fillRect(F.x - 6, F.y, F.w + 12, F.h); g.strokeStyle = c.gold; g.lineWidth = L.docked ? 5 : 3; g.strokeRect(F.x - 6, F.y, F.w + 12, F.h); }
      const ts = Math.round(F.h * 0.22);
      text(['THE BILL OF RIGHTS'], F.x + 10, F.y + 6, ts * 1.2, `${ts}px ${TYPE}`, c.brick);
      text([b.shown], F.x + F.w - 10, F.y + 6, ts * 1.2, `${ts}px ${TYPE}`, PENCIL, 'right');
      const top = F.y + ts * 1.2 + 14, bhh = F.h - (top - F.y) - 10, arrow = 44, avail = F.w - 20 - arrow * 2;
      const tot = items.reduce((a, it) => a + it[1], 0);
      let x = F.x + 10;
      items.forEach(([lab, v], i) => {
        const w = Math.round(avail * v / tot / 2) * 2, hh = Math.round(bhh * (0.72 + 0.28 * v / b.house) / 2) * 2;
        paperRect(x + w / 2, top + bhh / 2, w, hh, i === 2 ? c.navy : c.brick, { seed: 2030 + i, depth: 0.6, boil: false });
        const fs = fit(`${lab} ${v}`, w - 16, Math.round(hh * 0.5), SLAB);
        text([`${lab} ${v}`], x + w / 2, top + bhh / 2 - fs * 0.6, fs * 1.2, `${fs}px ${SLAB}`, c.cream, 'center');
        x += w;
        if (i < 2) {   // an ink arrow between the boxes (drawn, not a glyph)
          const ay = top + bhh / 2, ax0 = x + 8, ax1 = x + arrow - 8;
          ink([[ax0, ay], [ax1, ay]], { w: 4, color: INK, seed: 2040 + i, passes: 1, step: 20, amp: 0.3 });
          ink([[ax1 - 12, ay - 10], [ax1, ay], [ax1 - 12, ay + 10]], { w: 4, color: INK, seed: 2045 + i, passes: 1, step: 10, amp: 0.3 });
          x += arrow;
        }
      });
      g.restore();
    }

    drawCard(x, y, w, h, seed) { CV().parchment(x + w / 2, y + h / 2, Math.round(w / 10) * 10, Math.round(h / 10) * 10, { seed, depth: 1.3 }); }

    /* the question card (landscape / desktop) */
    drawCardQ() {
      const R = this.card(), { X, W, rows, h, top, padArea } = R, c = C();
      const t0 = this.phase === 'done' ? this.revealAt : this.qAt, s = pop(T, t0, 0.25, 1.4);
      if (s <= 0) return;
      const q = this.inst, st = this.st[this.k], lx = X + 36;
      g.save(); g.translate(X + W / 2, top + h / 2); g.scale(s, s); g.translate(-(X + W / 2), -(top + h / 2));
      this.drawCard(X, top, W, h, 2100 + (this.phase === 'done' ? 1 : 0));
      rows.forEach(r => {
        const y = top + r.y;
        if (r.kind === 'label') text(r.lines, lx, y, r.lh, `${r.size}px ${TYPE}`, c.brick);
        else if (r.kind === 'q') text(r.lines, lx, y, r.lh, `600 ${r.size}px ${SERIF}`, c.navy);
        else if (r.kind === 'head') text(r.lines, lx, y, r.lh, `${r.size}px ${SLAB}`, (this.results[this.round] || {}).star ? c.navy : c.brick);
        else if (r.kind === 'ans') text(r.lines, lx, y, r.lh, `bold ${r.size}px ${SERIF}`, INK);
        else if (r.kind === 'work') r.lines.forEach((l, j) => hand(l, lx, y + (j + 0.5) * r.lh, r.size, (this.phase === 'done' ? this.revealAt + 0.3 : this.stepAt + 0.1) + j * 0.25, { align: 'left', color: c.navy, seed: 2110 + j, dur: 0.3 }));
        else if (r.kind === 'why') r.lines.forEach((l, j) => hand(l, lx, y + (j + 0.5) * r.lh, r.size, this.pickedAt + 0.1 + j * 0.2, { align: 'left', color: c.brick, seed: 2120 + j, dur: 0.3 }));
        else if (r.kind === 'opt') this.drawOpt(r, X, W, y, s, q, st);
      });
      if (padArea) this.drawPadArea(padArea, X, W, top, s, q, st);
      g.restore();
      // Next
      if (R.bh && this.nextReady()) {
        const bh = UI.tap(96), label = this.nextLabel();
        if (UI.button('rd-next', label, X + W / 2, top + h + 22 + bh / 2, { w: 460, h: 96, color: c.brick, size: 44 })) this.next();
      }
    }
    nextReady() {
      const at = this.phase === 'done' ? this.revealAt + 0.5 : this.stepAt;
      return at != null && Game.time - at > NEXT_GUARD + 0.2;
    }
    nextLabel() {
      if (this.phase === 'ask') return 'Next question';
      return this.round < this.n - 1 ? 'Next round' : 'See my stars';
    }
    drawOpt(r, X, W, y, s, q, st) {
      const c = C(), i = r.i, rh = r.rh, cyy = y + rh / 2, crossed = st.tries.includes(i) && i !== q.answerIndex, right = st.done && i === q.answerIndex;
      const live = !st.done && !crossed && s > 0.98;
      const hit = live ? UI._hit(`rd-opt${this.round}_${this.k}_${i}`, X + W / 2, cyy, W - 30, rh) : { hover: false, clicked: false };
      const bg = hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : right ? '#e9eefb' : PAL.cream;
      paperRect(X + W / 2, cyy, Math.round((W - 44) / 10) * 10, Math.round((rh - 8) / 2) * 2, bg, { seed: 2130 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
      g.save(); g.fillStyle = crossed ? PENCIL : right ? c.navy : c.brick; g.fillRect(X + 36, cyy - 22, 44, 44);
      g.font = `26px ${SLAB}`; g.fillStyle = c.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 58, cyy + 1); g.restore();
      text(r.lines, X + 98, cyy - r.lines.length * r.lh / 2, r.lh, `${r.size}px ${SERIF}`, crossed ? PENCIL : INK);
      if (crossed) {
        const y0 = cyy - r.lines.length * r.lh / 2, a = st.tries[st.tries.length - 1] === i ? this.pickedAt : -1e9;
        r.lines.forEach((l, j) => {
          const mw = measure(l, `${r.size}px ${SERIF}`), ly = y0 + (j + 0.5) * r.lh;
          ink([[X + 92, ly + 6], [X + 106 + mw, ly - 6]], { w: 5, color: c.brick, seed: 2140 + i * 5 + j, prog: clamp((T - a - j * 0.15) / 0.3), passes: 1 });
        });
      }
      if (right) inkCircle(X + 58, cyy, 34, { w: 4, color: c.navy, seed: 2150 + i, passes: 1 });
      if (hit.clicked) this.choose(i);
    }
    drawPadArea(A, X, W, top, s, q, st) {
      const c = C(), lx = X + 36, y0 = top + A.y;
      // the display
      const dw = Math.round(A.leftW / 10) * 10, dh = A.dh;
      paperRect(lx + dw / 2, y0 + dh / 2, dw, dh, '#fbf6ea', { seed: 2160, depth: 0.5, boil: false });
      g.save(); g.strokeStyle = 'rgba(43,36,32,0.55)'; g.lineWidth = 3; g.strokeRect(lx + 8, y0 + 8, dw - 16, dh - 16); g.restore();
      const shake = Game.time - this.bumpAt < 0.3 ? Math.sin((Game.time - this.bumpAt) * 60) * 8 : 0;
      const ds = Math.round(dh * 0.58);
      g.save(); g.font = `${ds}px ${TYPE}`; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (st.done) {
        g.fillStyle = st.right ? c.navy : c.brick; g.fillText(q.answerText, lx + 34, y0 + dh / 2 + 2);
      } else if (this.typed) {
        g.fillStyle = INK; g.fillText(this.typed, lx + 34 + shake, y0 + dh / 2 + 2);
        const tw = measure(this.typed, `${ds}px ${TYPE}`);
        if (Math.floor(Game.time * 2) % 2 === 0) g.fillRect(lx + 40 + tw + shake, y0 + dh * 0.25, 4, dh * 0.5);
      } else {
        g.fillStyle = 'rgba(91,100,117,0.55)'; g.fillText('?', lx + 34 + shake, y0 + dh / 2 + 2);
        if (Math.floor(Game.time * 2) % 2 === 0) { g.fillStyle = INK; g.fillRect(lx + 34 + ds * 0.7 + shake, y0 + dh * 0.25, 4, dh * 0.5); }
      }
      g.restore();
      if (st.done && st.right) inkCircle(lx + dw - dh * 0.5, y0 + dh / 2, dh * 0.32, { w: 4, color: c.navy, seed: 2161, passes: 1 });
      // the notes under the display: why / retry / the working / the key hint
      A.left.forEach(r => {
        const y = y0 + r.y;
        const col = r.kind === 'work' ? c.navy : r.kind === 'hint' ? PENCIL : c.brick;
        if (r.kind === 'hint') text(r.lines, lx, y, r.lh, `${r.size}px ${TYPE}`, col);
        else r.lines.forEach((l, j) => hand(l, lx, y + (j + 0.5) * r.lh, r.size, (r.kind === 'work' ? this.stepAt + 0.1 : this.pickedAt + 0.1) + j * 0.22, { align: 'left', color: col, seed: 2170 + j + (r.kind === 'work' ? 10 : 0), dur: 0.3 }));
      });
      if (st.done || s < 0.98) return;
      // the pad
      const px = X + W - 36 - A.padW, fsz = Math.round(A.kh * 0.46);
      PAD.forEach((id, j) => {
        const col = j % 3, row = Math.floor(j / 3);
        const kx = px + col * (A.kw + A.gap) + A.kw / 2, ky = y0 + row * (A.kh + A.gap) + A.kh / 2;
        const label = id === 'back' ? '' : id === 'enter' ? 'ENTER' : id;
        const color = id === 'enter' ? c.brick : id === 'back' ? PENCIL : c.navy;
        const hit = UI.button('rd-key-' + id, label, kx, ky, { w: A.kw, h: A.kh, fixed: true, color, size: id === 'enter' ? Math.round(A.kh * 0.34) : fsz, font: id === 'enter' ? SLAB : TYPE });
        if (id === 'back') this.drawBackIcon(kx, ky, A.kh * 0.34, c.cream);
        const ka = this.keyAt[id];
        if (ka != null && Game.time - ka < 0.12) { g.save(); g.globalAlpha = 0.35; g.fillStyle = '#fff6d8'; g.fillRect(kx - A.kw / 2, ky - A.kh / 2, A.kw, A.kh); g.restore(); }
        if (hit) this.press(id);
      });
    }
    /* ⌫ drawn in ink (the fonts have no such glyph): a key-cap arrow pointing left with an x */
    drawBackIcon(x, y, r, color) {
      g.save(); g.strokeStyle = color; g.lineWidth = Math.max(3, r * 0.14); g.lineJoin = 'round'; g.lineCap = 'round';
      g.beginPath(); g.moveTo(x - r * 1.2, y); g.lineTo(x - r * 0.5, y - r * 0.7); g.lineTo(x + r * 1.1, y - r * 0.7); g.lineTo(x + r * 1.1, y + r * 0.7); g.lineTo(x - r * 0.5, y + r * 0.7); g.closePath(); g.stroke();
      g.beginPath(); g.moveTo(x - r * 0.05, y - r * 0.3); g.lineTo(x + r * 0.6, y + r * 0.3); g.moveTo(x + r * 0.6, y - r * 0.3); g.lineTo(x - r * 0.05, y + r * 0.3); g.stroke();
      g.restore();
    }

    /* ---- portrait: the ledger is the picture; everything to read and tap is in the dock ---- */
    drawDock() {
      if (this.phase === 'intro') {
        Dock.heading('rd-intro', TITLE, { cls: 'big' });
        Dock.text('rd-intro', this.introLines());
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start the first round', go: true, color: 'red', onClick: () => this.start() });
        return;
      }
      if (this.phase === 'end') return this.dockEnd();
      const r = this.cur, id = this.attempt + '_' + this.round, c = C();
      Dock.heading('rd-r' + id, `Round ${this.round + 1} of ${this.n}: ${r.title}`);
      Dock.text('rd-when' + id, [r.state ? `${Ratify.name(r.state)}, ${this.dateOf(r)}. Ratified so far: ${this.filled()} of ${Ratify.article7} needed.` : `The Bill of Rights: House ${Ratify.bor.house}, Senate ${Ratify.bor.senate}, states ${Ratify.bor.ratified}.`], { cls: 'small' });
      if (this.phase === 'done') {
        const res = this.results[this.round] || { star: 0 };
        Dock.text('rd-star' + id, [res.star ? 'Round won: +1 star.' : 'Round done. No star this time.'], { cls: res.star ? 'right' : 'wrong' });
        r.steps.forEach((q, j) => Dock.text('rd-work' + id + '_' + j, [`${r.steps.length > 1 ? (j + 1) + '. ' : ''}Answer: ${q.answerText}. ${q.explain}`], { cls: 'note', reveal: true }));
        Dock.button({ id: 'next', glyph: this.nextLabel() + ' ▶', label: 'next', aria: this.nextLabel(), go: true, onClick: () => this.next() });
        return;
      }
      const q = this.inst, st = this.st[this.k], sid = id + '_' + this.k, last = st.tries[st.tries.length - 1];
      const qn = r.steps.length > 1 ? `Question ${this.k + 1} of ${r.steps.length}` : '';
      if (q.answer === 'numpad') {
        if (qn) Dock.text('rd-qn' + sid, [qn], { cls: 'small' });
        Dock.text('rd-q' + sid, [q.text]);
        if (st.done) {
          Dock.text('rd-ans' + sid, [st.right ? `Right: ${q.answerText}.` : `Not ${last}. The answer is ${q.answerText}.`], { cls: st.right ? 'right' : 'wrong' });
          Dock.text('rd-work' + sid, [q.explain], { cls: 'note', reveal: true });
          Dock.button({ id: 'next', glyph: this.nextLabel() + ' ▶', label: 'next', aria: this.nextLabel(), go: true, onClick: () => this.next() });
          return;
        }
        if (last != null) Dock.text('rd-why' + sid + '_' + st.tries.length, [`Not ${last}. ${Bank.why(q, last) || 'Check your working.'} Try again.`], { cls: 'wrong', reveal: true });
        Dock.text('rd-typed' + sid, [`Your answer: ${this.typed || '?'}`], { cls: 'center' });
        const k = id => ({ label: id === 'back' ? '⌫' : id === 'enter' ? 'ENTER' : id, color: id === 'enter' ? c.brick : id === 'back' ? PENCIL : c.navy, onClick: () => this.press(id) });
        // three rows so ENTER stays above the fold: 1–5, 6–0, then ⌫ ENTER
        DOCK_PAD.forEach((row, j) => Dock.buttons('rd-pad' + j, row.map(k)));
        return;
      }
      const state = q.options.map((o, i) => (st.done && i === q.answerIndex ? 'right' : st.tries.includes(i) ? 'wrong' : st.done ? 'faded' : ''));
      if (qn) Dock.text('rd-qn' + sid, [qn], { cls: 'small' });
      Dock.choices('rd-c' + sid, { prompt: q.text, options: q.options, state, locked: st.done, onChoose: i => this.choose(i) });
      if (st.done) {
        Dock.text('rd-work' + sid, [q.explain], { cls: 'right', reveal: true });
        Dock.button({ id: 'next', glyph: this.nextLabel() + ' ▶', label: 'next', aria: this.nextLabel(), go: true, onClick: () => this.next() });
      } else if (last != null) Dock.text('rd-why' + sid + '_' + st.tries.length, ['Not quite. ' + (q.why[last] || q.explain)], { cls: 'wrong', reveal: true });
    }
    dockEnd() {
      const s = this.stars, c = C(), btns = this.endButtons();
      Dock.heading('rd-end', `Your stars: ${s} / ${this.of}`, { cls: 'big' });
      Dock.text('rd-endmsg', [this.endMsg()]);
      Dock.list('rd-endl', this.rounds.map((r, i) => { const res = this.results[i] || { star: 0 }; return { badge: String(i + 1), color: res.star ? c.navy : c.brick, lines: [r.title, res.star ? '1 star' : 'no star'] }; }));
      Dock.text('rd-code', [`Quiz code ${this.seed}`], { cls: 'small' });
      Dock.buttons('rd-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
    }
    endMsg() { return this.passed ? 'Passed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New rounds?`; }

    introLines() {
      return ['Six rounds on the real ratification record, in order: the votes, the deals and the Bill of Rights.',
        'Each round lights up its state on the ledger. Answer, and watch the count climb to nine.',
        'Some answers go on the number pad. A wrong try gets a hint and another go.',
        `Every question in a round right on the first try = 1 star. ${this.passStars} of ${this.of} stars for the stamp.`];
    }
    drawIntro() {
      const t0 = this.enteredAt, c = C(), X = 866, W = 1030, fs = Math.round(UI.text(34));
      const lines = this.introLines().map(l => wrap(l, W - 80, `${fs}px ${SERIF}`));
      const bh = UI.tap(104), head = 120;
      const ch = head + lines.reduce((a, l) => a + l.length, 0) * fs * 1.4 + lines.length * 12 + 40;
      const top = Math.max(minTop(), Math.min(minTop() + 20, BOTTOM - bh - 30 - ch));
      this.drawCard(X, top, W, ch, 2200);
      CV().woodtype('ROAD TO NINE', X + W / 2, top + 64, 62, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 2201 });
      let y = top + head;
      lines.forEach((ls, i) => {
        ls.forEach(tl => {
          if (T >= t0 + 0.5 + i * 0.25) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? c.brick : INK; g.textAlign = 'left'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.25) / 0.3); g.fillText(tl, X + 40, y + fs * 0.7); g.restore(); }
          y += fs * 1.4;
        });
        y += 12;
      });
      if (this.t > 0.8 && UI.button('rd-start', 'Start', X + W / 2, Math.min(BOTTOM - bh / 2, top + ch + 24 + bh / 2), { w: 420, h: 104, color: c.brick, size: 54 })) this.start();
    }
    drawEnd() {
      const s = this.stars, c = C(), X = 866, W = 1030, btns = this.endButtons(), top = minTop();
      const bh = UI.tap(96), rs = Math.round(UI.text(30)), rowH = Math.max(rs * 1.5, 50);
      const ch = BOTTOM - top - bh - 30;
      this.drawCard(X, top, W, ch, 2300);
      CV().woodtype(`${s} / ${this.of}`, X + 300, top + 80, 84, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 2301 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', X + W - 250, top + 84, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 0.62, color: c.brick });
      let y = top + 170;
      this.rounds.forEach((r, i) => {
        const res = this.results[i] || { star: 0 }, a = this.endAt + 0.3 + i * 0.1;
        if (T < a) return;
        g.save(); g.globalAlpha = clamp((T - a) / 0.25);
        g.fillStyle = res.star ? c.navy : c.brick; g.fillRect(X + 40, y + rowH / 2 - 20, 40, 40);
        g.font = `24px ${SLAB}`; g.fillStyle = c.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), X + 60, y + rowH / 2 + 1);
        g.restore();
        text([r.title + (r.state ? `  (${Ratify.name(r.state)})` : '')], X + 100, y, rowH, `${rs}px ${SERIF}`, INK);
        hand(res.star ? '1 star' : 'no star', X + W - 50, y + rowH / 2, rs, a + 0.1, { align: 'right', color: res.star ? c.navy : c.brick, seed: 2310 + i, dur: 0.25 });
        y += rowH;
      });
      hand(this.endMsg(), X + W / 2, Math.min(top + ch - 50, y + 50), Math.round(UI.text(38)), this.endAt + 0.9, { color: c.brick, seed: 2320, dur: 0.5 });
      hand(`quiz code ${this.seed}`, X + W - 40, top + ch - 22, 26, this.endAt + 0.6, { align: 'right', color: PENCIL, seed: 2325 });
      const by = BOTTOM - bh / 2;
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('rd-end' + i, b.label, X + W / 2 + (i ? 1 : -1) * 258, by, { w: 496, h: 96, color: i === 0 ? c.brick : c.navy, size: 40 })) b.go(); });
    }
  }

  Road.STEP = STEP;
  Object.defineProperty(Road, 'solution', { get() { const s = Game.current(); return s instanceof Road ? s.solution : null; } });
  /* tests / screenshots: swap the attempt's rounds for these item ids (the model builds them), sorted by order */
  Road.preview = (ids, seed = 1) => {
    const s = Game.current(); if (!(s instanceof Road) || !s.rounds) return false;
    const items = json().items, r = Bank.rng(seed);
    const got = ids.map(id => items.find(x => x.id === id)).filter(Boolean).sort((a, b) => (a.order || 0) - (b.order || 0));
    got.forEach((it, i) => { if (i < s.rounds.length) s.rounds[i] = Ratify.roadRound(it, r); });
    s._qc = null; s._cuts = null;
    if (s.phase === 'ask') s.setup(s.round), s.phase = 'ask';
    return true;
  };
  Levels.road = Road;
})();
